import React, {
  useState,
  useCallback,
  useRef,
  useImperativeHandle,
  useEffect,
} from 'react';
import { HotTable, HotTableRef } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';
import { Spinner } from 'src/components/ui/spinner';
import { Button as ShadcnButton } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Badge } from 'src/components/ui/badge';
import {
  MagnifyingGlassIcon,
  WarningCircleIcon,
  CaretUpIcon,
  CaretDownIcon,
  TextTIcon,
  XIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  DownloadSimpleIcon,
} from '@phosphor-icons/react';
import { HyperFormula } from 'hyperformula';
import Handsontable from 'handsontable';
import Papa from 'papaparse';
import { T, Var, useGT } from 'gt-react';

registerAllModules();

const hyperformulaInstance = HyperFormula.buildEmpty({
  licenseKey: 'internal-use-in-handsontable',
});

const globalSearchResults: SearchResult[] = [];
let globalSearchIndex = -1;

function highlightUnknownAndSearchRenderer(
  instance: Handsontable,
  td: HTMLTableCellElement,
  row: number,
  col: number,
  prop: string | number,
  value: unknown,
  cellProperties: Handsontable.CellProperties,
) {
  Handsontable.renderers.TextRenderer(
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties,
  );

  td.style.backgroundColor = '';
  td.style.border = '';
  td.style.color = '';
  td.style.fontWeight = '';

  if (value === 'UNKNOWN') {
    td.style.color = '#c62828';
    td.style.fontWeight = 'bold';
  }

  const isSearchResult = globalSearchResults.some(
    (result) => result.row === row && result.col === col,
  );

  if (isSearchResult) {
    const resultIndex = globalSearchResults.findIndex(
      (result) => result.row === row && result.col === col,
    );

    if (resultIndex === globalSearchIndex) {
      td.style.backgroundColor = '#ffeb3b';
      td.style.border = '2px solid #f57f17';
    } else {
      td.style.backgroundColor = '#fff3c4';
      td.style.border = '1px solid #fbc02d';
    }
  }
}

Handsontable.renderers.registerRenderer(
  'highlightUnknownAndSearch',
  highlightUnknownAndSearchRenderer,
);

interface SelectionSummary {
  sum: number;
  count: number;
  average: number;
  min: number;
  max: number;
  numericCount: number;
}

interface SearchResult {
  row: number;
  col: number;
  value: string;
}

interface SpreadsheetViewerProps {
  data: unknown[][];
  loading?: boolean;
  error?: string | null;
  height?: string | number;
  colHeaders?: boolean | string[];
  rowHeaders?: boolean;
  afterGetColHeader?: (col: number, TH: HTMLElement) => void;
  id?: string;
  showSelectionSummary?: boolean;
  onDataChange?: (data: unknown[][]) => void;
  filename?: string;
  readOnly?: boolean;
}

export interface SpreadsheetViewerRef extends HotTableRef {
  getSortedData: () => unknown[][];
}

const SpreadsheetViewer = React.forwardRef<
  SpreadsheetViewerRef,
  SpreadsheetViewerProps
>(
  (
    {
      data,
      loading = false,
      error = null,
      height = '100%',
      colHeaders = true,
      rowHeaders = true,
      afterGetColHeader,
      id,
      showSelectionSummary = false,
      onDataChange,
      filename,
      readOnly = false,
    },
    ref,
  ) => {
    const gt = useGT();
    const searchPlaceholderText = gt('Search in spreadsheet...', {
      context: 'placeholder, spreadsheet search',
    });
    const caseSensitiveTitle = gt('Case sensitive', {
      context: 'tooltip, case sensitive spreadsheet search',
    });
    const zoomOutTitle = gt('Zoom Out (Ctrl/Cmd + -)', {
      context: 'tooltip, zoom out spreadsheet',
    });
    const zoomResetTitle = gt('Reset Zoom (Ctrl/Cmd + 0)', {
      context: 'tooltip, reset spreadsheet zoom',
    });
    const zoomInTitle = gt('Zoom In (Ctrl/Cmd + +)', {
      context: 'tooltip, zoom in spreadsheet',
    });
    const downloadCsvTitle = gt('Download CSV', {
      context: 'tooltip, download spreadsheet as CSV',
    });

    const processedData = React.useMemo(() => {
      if (!data || data.length === 0) {
        return { tableData: data || [], headers: colHeaders };
      }

      if (colHeaders === true && data.length > 0) {
        const headers = data[0].map((cell) => String(cell || ''));
        const tableData = data.slice(1); // Remove first row from data
        return { tableData, headers };
      }

      return { tableData: data, headers: colHeaders };
    }, [data, colHeaders]);

    const columnsConfig = React.useMemo(() => {
      if (!processedData.tableData.length) return [];

      const columnCount = processedData.tableData[0]?.length || 0;
      return Array.from({ length: columnCount }, () => ({
        renderer: 'highlightUnknownAndSearch',
        readOnly: readOnly,
      }));
    }, [processedData.tableData, readOnly]);

    const [selectionSummary, setSelectionSummary] =
      useState<SelectionSummary | null>(null);

    const [showSearch, setShowSearch] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
    const [caseSensitive, setCaseSensitive] = useState(false);

    const [zoomLevel, setZoomLevel] = useState(100); // Percentage

    const hotTableRef = useRef<HotTableRef>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const getSortedData = useCallback((): unknown[][] => {
      if (!hotTableRef.current) return data;

      try {
        const hotInstance = hotTableRef.current.hotInstance;
        if (!hotInstance) return data;

        const sortedTableData = hotInstance.getData();

        if (colHeaders === true && processedData.headers) {
          return [processedData.headers, ...sortedTableData];
        } else {
          return sortedTableData;
        }
      } catch (error) {
        console.warn('Error getting sorted data:', error);
        return data;
      }
    }, [data, colHeaders, processedData.headers]);

    const navigateToSearchResult = useCallback(
      (index: number, results: SearchResult[] = searchResults) => {
        if (
          index < 0 ||
          index >= results.length ||
          !hotTableRef.current?.hotInstance
        )
          return;

        const result = results[index];
        const hotInstance = hotTableRef.current.hotInstance;

        // Only scroll to the cell, don't select it to keep focus in search input
        hotInstance.scrollViewportTo(result.row, result.col);

        globalSearchIndex = index;

        hotInstance.render();
      },
      [searchResults],
    );

    const performSearch = useCallback(
      (term: string, isCaseSensitive = false, autoNavigate = false) => {
        if (!term.trim() || !hotTableRef.current?.hotInstance) {
          setSearchResults([]);
          setCurrentSearchIndex(-1);
          return;
        }

        const hotInstance = hotTableRef.current.hotInstance;
        const tableData = hotInstance.getData();
        const results: SearchResult[] = [];

        const searchValue = isCaseSensitive ? term : term.toLowerCase();

        tableData.forEach((row: unknown[], rowIndex: number) => {
          row.forEach((cell: unknown, colIndex: number) => {
            if (cell !== null && cell !== undefined) {
              const cellValue = String(cell);
              const compareValue = isCaseSensitive
                ? cellValue
                : cellValue.toLowerCase();

              if (compareValue.includes(searchValue)) {
                results.push({
                  row: rowIndex,
                  col: colIndex,
                  value: cellValue,
                });
              }
            }
          });
        });

        setSearchResults(results);
        const newIndex = autoNavigate && results.length > 0 ? 0 : -1;
        setCurrentSearchIndex(newIndex);

        globalSearchResults.length = 0;
        globalSearchResults.push(...results);
        globalSearchIndex = newIndex;

        if (hotTableRef.current?.hotInstance) {
          hotTableRef.current.hotInstance.render();
        }

        if (autoNavigate && results.length > 0) {
          navigateToSearchResult(0, results);
        }
      },
      [navigateToSearchResult],
    );

    const handleSearchNext = useCallback(() => {
      if (searchResults.length === 0) return;

      const nextIndex =
        currentSearchIndex === -1
          ? 0
          : (currentSearchIndex + 1) % searchResults.length;
      setCurrentSearchIndex(nextIndex);
      navigateToSearchResult(nextIndex);
    }, [currentSearchIndex, searchResults, navigateToSearchResult]);

    const handleSearchPrevious = useCallback(() => {
      if (searchResults.length === 0) return;

      const prevIndex =
        currentSearchIndex === -1
          ? searchResults.length - 1
          : currentSearchIndex <= 0
            ? searchResults.length - 1
            : currentSearchIndex - 1;
      setCurrentSearchIndex(prevIndex);
      navigateToSearchResult(prevIndex);
    }, [currentSearchIndex, searchResults, navigateToSearchResult]);

    const handleSearchChange = useCallback(
      (value: string) => {
        setSearchTerm(value);

        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }

        if (!value.trim()) {
          setSearchResults([]);
          setCurrentSearchIndex(-1);
          globalSearchResults.length = 0;
          globalSearchIndex = -1;
          if (hotTableRef.current?.hotInstance) {
            hotTableRef.current.hotInstance.render();
          }
          return;
        }

        searchTimeoutRef.current = setTimeout(() => {
          performSearch(value, caseSensitive, false);
        }, 300); // 300ms delay
      },
      [performSearch, caseSensitive],
    );

    useEffect(() => {
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, []);

    const handleCloseSearch = useCallback(() => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }

      setShowSearch(false);
      setSearchTerm('');
      setSearchResults([]);
      setCurrentSearchIndex(-1);

      globalSearchResults.length = 0;
      globalSearchIndex = -1;

      if (hotTableRef.current?.hotInstance) {
        hotTableRef.current.hotInstance.render();
      }

      if (hotTableRef.current?.hotInstance) {
        hotTableRef.current.hotInstance.listen();
      }
    }, []);

    const handleZoomIn = useCallback(() => {
      setZoomLevel((prev) => Math.min(prev + 10, 200)); // Max 200%
    }, []);

    const handleZoomOut = useCallback(() => {
      setZoomLevel((prev) => Math.max(prev - 10, 50)); // Min 50%
    }, []);

    const handleZoomReset = useCallback(() => {
      setZoomLevel(100);
    }, []);

    const tableContainerStyle = React.useMemo(
      () => ({
        outline: 'none',
        height: '100%',
        width: '100%',
        border: showSearch ? '2px solid #1976d2' : '2px solid transparent',
        borderRadius: '4px',
        transition: 'border-color 0.2s ease-in-out',
        overflow: 'hidden', // Prevent scrollbars from zoom
      }),
      [showSearch],
    );

    const tableStyle = React.useMemo(
      () => ({
        height: `${100 / (zoomLevel / 100)}%`, // Scale container to fill space
        width: `${100 / (zoomLevel / 100)}%`,
        transform: `scale(${zoomLevel / 100})`,
        transformOrigin: 'top left',
      }),
      [zoomLevel],
    );

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        const target = event.target as HTMLElement;
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        ) {
          return;
        }

        const activeElement = document.activeElement as HTMLElement | null;
        const isActiveInSearchBar =
          !!activeElement?.closest('[data-search-bar]');
        const isActiveInTable = !!(
          activeElement && tableContainerRef.current?.contains(activeElement)
        );
        if (!isActiveInSearchBar && !isActiveInTable) {
          return;
        }

        if (hotTableRef.current?.hotInstance) {
          const activeEditor =
            hotTableRef.current.hotInstance.getActiveEditor();
          if (activeEditor && activeEditor.isOpened()) {
            return; // Don't interfere with cell editing
          }
        }

        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
          event.preventDefault();

          if (showSearch) {
            handleCloseSearch();
          } else {
            if (hotTableRef.current?.hotInstance) {
              hotTableRef.current.hotInstance.deselectCell();
            }

            setShowSearch(true);
            // Focus immediately without delay to prevent keystrokes from going to cells
            // Use requestAnimationFrame to ensure the search input is rendered first
            requestAnimationFrame(() => {
              searchInputRef.current?.focus();
              searchInputRef.current?.select();
            });
          }
        }

        if (event.ctrlKey || event.metaKey) {
          if (event.key === '=' || event.key === '+') {
            event.preventDefault();
            handleZoomIn();
          } else if (event.key === '-') {
            event.preventDefault();
            handleZoomOut();
          } else if (event.key === '0') {
            event.preventDefault();
            handleZoomReset();
          }
        }

        if (event.key === 'Escape' && showSearch) {
          handleCloseSearch();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [
      showSearch,
      handleCloseSearch,
      handleZoomIn,
      handleZoomOut,
      handleZoomReset,
    ]);

    useEffect(() => {
      if (showSearch && searchInputRef.current) {
        // Don't deselect cells - let user keep their selection while searching
        searchInputRef.current.focus();
        searchInputRef.current.select();
      }
    }, [showSearch]);

    const handleCaseSensitiveToggle = useCallback(() => {
      const newCaseSensitive = !caseSensitive;
      setCaseSensitive(newCaseSensitive);

      if (searchTerm.trim()) {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }

        performSearch(searchTerm, newCaseSensitive, false);
      }
    }, [caseSensitive, searchTerm, performSearch]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        const isClickingOnTable = tableContainerRef.current?.contains(target);
        const isClickingOnSearchBar = (target as Element)?.closest(
          '[data-search-bar]',
        );

        if (!isClickingOnTable && !isClickingOnSearchBar && showSearch) {
          handleCloseSearch();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [showSearch, handleCloseSearch]);

    const calculateSelectionSummary = useCallback(
      (selectedData: unknown[][]): SelectionSummary => {
        const flatData = selectedData.flat();
        const numericValues: number[] = [];

        flatData.forEach((value) => {
          if (value !== null && value !== undefined && value !== '') {
            const numValue =
              typeof value === 'number' ? value : parseFloat(String(value));
            if (!isNaN(numValue) && isFinite(numValue)) {
              numericValues.push(numValue);
            }
          }
        });

        const sum = numericValues.reduce((acc, val) => acc + val, 0);
        const count = flatData.filter(
          (val) => val !== null && val !== undefined && val !== '',
        ).length;
        const numericCount = numericValues.length;
        const average = numericCount > 0 ? sum / numericCount : 0;
        const min = numericCount > 0 ? Math.min(...numericValues) : 0;
        const max = numericCount > 0 ? Math.max(...numericValues) : 0;

        return {
          sum,
          count,
          average,
          min,
          max,
          numericCount,
        };
      },
      [],
    );

    const handleAfterSelection = useCallback(
      (row: number, column: number, row2: number, column2: number) => {
        if (!showSelectionSummary || !hotTableRef.current) return;

        try {
          const hotInstance = hotTableRef.current?.hotInstance;
          if (!hotInstance) return;

          const startRow = Math.min(row, row2);
          const endRow = Math.max(row, row2);
          const startCol = Math.min(column, column2);
          const endCol = Math.max(column, column2);

          const selectedData = hotInstance.getData(
            startRow,
            startCol,
            endRow,
            endCol,
          );
          const summary = calculateSelectionSummary(selectedData);
          setSelectionSummary(summary);
        } catch (error) {
          console.warn('Error calculating selection summary:', error);
          setSelectionSummary(null);
        }
      },
      [showSelectionSummary, calculateSelectionSummary],
    );

    const handleAfterDeselect = useCallback(() => {
      if (showSelectionSummary) {
        setSelectionSummary(null);
      }
    }, [showSelectionSummary]);

    const handleAfterColumnSort = useCallback(
      (currentSortConfig: unknown, destinationSortConfigs: unknown) => {
        console.log(
          'Column sorted:',
          currentSortConfig,
          'New sort configs:',
          destinationSortConfigs,
        );

        // Don't call setData immediately to avoid performance issues
        // The sorted data can be accessed via getSortedData() when needed (e.g., for downloads)
      },
      [],
    );

    const handleAfterChange = useCallback(
      (changes: Handsontable.CellChange[] | null, source: string) => {
        if (source === 'edit' && onDataChange && hotTableRef.current) {
          const currentData = hotTableRef.current.hotInstance?.getData() || [];
          if (colHeaders === true && processedData.headers) {
            const fullData = [processedData.headers, ...currentData];
            onDataChange(fullData);
          } else {
            onDataChange(currentData);
          }
        }
      },
      [onDataChange, colHeaders, processedData.headers],
    );

    useImperativeHandle(
      ref,
      // @ts-expect-error
      () => ({
        ...hotTableRef.current,
        getSortedData,
      }),
      [getSortedData],
    );

    const hotTable = React.useMemo(
      () => (
        <div ref={tableContainerRef} style={tableContainerStyle}>
          <div style={tableStyle}>
            <HotTable
              ref={hotTableRef}
              data={processedData.tableData}
              colHeaders={processedData.headers}
              rowHeaders={rowHeaders}
              height="100%"
              width="100%"
              licenseKey="non-commercial-and-evaluation"
              stretchH="all"
              autoWrapRow={true}
              autoWrapCol={true}
              rowHeights={24}
              contextMenu={readOnly ? false : true}
              columnSorting={true}
              manualColumnResize={true}
              manualRowResize={true}
              columns={columnsConfig}
              formulas={{
                engine: hyperformulaInstance,
                sheetName: 'Sheet1',
              }}
              afterGetColHeader={afterGetColHeader}
              afterSelection={
                showSelectionSummary ? handleAfterSelection : undefined
              }
              afterDeselect={
                showSelectionSummary ? handleAfterDeselect : undefined
              }
              afterColumnSort={handleAfterColumnSort}
              afterChange={readOnly ? undefined : handleAfterChange}
              // Fix editing UX issues - let Handsontable handle editing naturally
              enterBeginsEditing={readOnly ? false : true}
              enterMoves={readOnly ? undefined : { row: 1, col: 0 }}
              tabMoves={readOnly ? undefined : { row: 0, col: 1 }}
              outsideClickDeselects={false} // Keep selection when clicking outside
            />
          </div>
        </div>
      ),
      [
        tableContainerStyle,
        tableStyle,
        processedData.tableData,
        processedData.headers,
        rowHeaders,
        readOnly,
        columnsConfig,
        afterGetColHeader,
        showSelectionSummary,
        handleAfterSelection,
        handleAfterDeselect,
        handleAfterColumnSort,
        handleAfterChange,
      ],
    );
    if (loading) {
      return (
        <div className="flex items-center justify-center" style={{ height }}>
          <Spinner />
        </div>
      );
    }
    if (error) {
      return (
        <div
          className="flex flex-col items-center justify-center text-center p-6"
          style={{ height }}
        >
          <WarningCircleIcon size={48} className="mb-2 text-red-500" />
          <p className="text-base font-semibold text-red-600 mb-1">
            {gt('Error', { context: 'generic error heading' })}
          </p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      );
    }

    const searchBarComponent = showSearch ? (
      <div
        data-search-bar
        className="absolute top-2 right-2 z-[1001] p-2 min-w-[320px] bg-white/98 backdrop-blur-sm rounded-lg border shadow-md"
      >
        <div className="flex items-center gap-2">
          <div className="relative flex-grow">
            <MagnifyingGlassIcon
              size={16}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
            />
            <Input
              ref={searchInputRef}
              className="pl-7"
              placeholder={searchPlaceholderText}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchTerm) {
                  e.preventDefault();
                  if (e.shiftKey) {
                    handleSearchPrevious();
                  } else {
                    handleSearchNext();
                  }
                }
              }}
            />
          </div>

          {searchResults.length > 0 && (
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {currentSearchIndex === -1
                  ? `0/${searchResults.length}`
                  : `${currentSearchIndex + 1}/${searchResults.length}`}
              </Badge>
              <ShadcnButton
                variant="ghost"
                size="icon"
                onClick={handleSearchPrevious}
                disabled={searchResults.length === 0}
                aria-label={gt('Previous result', {
                  context: 'spreadsheet search navigation button',
                })}
              >
                <CaretUpIcon size={16} />
              </ShadcnButton>
              <ShadcnButton
                variant="ghost"
                size="icon"
                onClick={handleSearchNext}
                disabled={searchResults.length === 0}
                aria-label={gt('Next result', {
                  context: 'spreadsheet search navigation button',
                })}
              >
                <CaretDownIcon size={16} />
              </ShadcnButton>
            </div>
          )}

          <ShadcnButton
            variant="ghost"
            size="icon"
            onClick={handleCaseSensitiveToggle}
            title={caseSensitiveTitle}
            className={caseSensitive ? 'text-primary' : ''}
          >
            <TextTIcon size={16} />
          </ShadcnButton>

          <ShadcnButton
            variant="ghost"
            size="icon"
            onClick={handleCloseSearch}
            aria-label={gt('Close search', {
              context: 'spreadsheet search panel close button',
            })}
          >
            <XIcon size={16} />
          </ShadcnButton>
        </div>

        {searchTerm && searchResults.length === 0 && (
          <p className="mt-2 block text-xs text-muted-foreground">
            {gt('No results found', {
              context: 'spreadsheet search, no matches',
            })}
          </p>
        )}
      </div>
    ) : null;

    const footerComponent = (
      <div className="flex justify-between items-center px-4 py-2 border-t border-border bg-card min-h-[48px]">
        {/* Left side - Zoom controls */}
        <div className="flex items-center gap-2">
          <ShadcnButton
            variant="ghost"
            size="icon"
            onClick={handleZoomOut}
            disabled={zoomLevel <= 50}
            title={zoomOutTitle}
          >
            <MagnifyingGlassMinusIcon size={16} />
          </ShadcnButton>

          <span
            className="min-w-[60px] text-center text-sm font-medium cursor-pointer px-2 py-1 rounded hover:bg-accent"
            onClick={handleZoomReset}
            title={zoomResetTitle}
          >
            {zoomLevel}%
          </span>

          <ShadcnButton
            variant="ghost"
            size="icon"
            onClick={handleZoomIn}
            disabled={zoomLevel >= 200}
            title={zoomInTitle}
          >
            <MagnifyingGlassPlusIcon size={16} />
          </ShadcnButton>
        </div>

        {/* Right side - Download button and selection summary */}
        <div className="flex items-center gap-4">
          {/* Download button */}
          <ShadcnButton
            variant="outline"
            size="icon"
            onClick={() => {
              try {
                const dataToDownload = getSortedData();

                const csvContent = Papa.unparse(dataToDownload);

                const timestamp = new Date().toISOString().split('T')[0];
                const baseFilename = filename
                  ? filename.replace(/\.[^/.]+$/, '')
                  : 'spreadsheet-data';
                const downloadFilename = `${baseFilename}-${timestamp}.csv`;

                const blob = new Blob([csvContent], {
                  type: 'text/csv;charset=utf-8;',
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = downloadFilename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              } catch (error) {
                console.warn('Error downloading data:', error);
              }
            }}
            title={downloadCsvTitle}
            className="text-blue-500 border-blue-500 hover:bg-blue-50"
          >
            <DownloadSimpleIcon size={16} />
          </ShadcnButton>

          {showSelectionSummary && selectionSummary ? (
            <div className="flex gap-4 items-center">
              <span className="text-xs text-muted-foreground">
                <T context="selection summary, cell count">
                  Count:{' '}
                  <strong>
                    <Var>{selectionSummary.count}</Var>
                  </strong>
                </T>
              </span>
              {selectionSummary.numericCount > 0 && (
                <>
                  <span className="text-xs text-muted-foreground">
                    <T context="selection summary, sum of numbers">
                      Sum: <Var>{selectionSummary.sum.toLocaleString()}</Var>
                    </T>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    <T context="selection summary, average">
                      Avg: <Var>{selectionSummary.average.toFixed(2)}</Var>
                    </T>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    <T context="selection summary, minimum">
                      Min: <Var>{selectionSummary.min.toLocaleString()}</Var>
                    </T>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    <T context="selection summary, maximum">
                      Max: <Var>{selectionSummary.max.toLocaleString()}</Var>
                    </T>
                  </span>
                </>
              )}
            </div>
          ) : (
            // Empty space to maintain layout balance
            <div />
          )}
        </div>
      </div>
    );

    const tableWithOverlaysAndFooter = (
      <div className="flex flex-col h-full w-full">
        {/* Table area with search overlay */}
        <div className="relative grow min-h-0 overflow-hidden">
          {hotTable}
          {searchBarComponent}
        </div>

        {/* Fixed footer */}
        {footerComponent}
      </div>
    );

    if (id) {
      return (
        <div id={id} style={{ width: '100%', height: '100%' }}>
          {tableWithOverlaysAndFooter}
        </div>
      );
    }

    return tableWithOverlaysAndFooter;
  },
);

SpreadsheetViewer.displayName = 'SpreadsheetViewer';

export type { SpreadsheetViewerProps };
export default SpreadsheetViewer;
